import {
  createApi,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError
} from '@reduxjs/toolkit/query/react';
import {
  getAccessToken,
  refreshAccessToken
} from '@/app/common/utils/customFetchBase';
import { getBaseApiPath } from '@/app/common/api/config';
import type {
  ListMcpChatSkillsCatalogArgs,
  ListMcpPromptTemplatesArgs,
  ListMcpThreadsArgs,
  McpChatSkillCatalogResult,
  McpChatSkillCategoriesResult,
  McpChatSkillsListResult,
  McpChatSuggestionsResult,
  McpHelpAskResult,
  McpMessageFeedback,
  McpMessageFeedbackRating,
  McpMessageFeedbackReason,
  McpProjectAiModel,
  McpProjectAiModelProvider,
  McpAutoBudget,
  McpPromptCategory,
  McpPromptTemplate,
  McpPromptTemplatesListResult,
  McpPromptTemplateUpsert,
  McpPromptVariable,
  McpThreadsListResult,
  McpThreadDetail,
  McpToolType,
  McpToolLabelsResult,
  McpUserAiSettings,
  McpChatOnboardingState,
  PatchMcpChatOnboardingArgs
} from '../types/mcpApiTypes';

const MCP_API_BASE = `${getBaseApiPath()}/mcp/v1`;

function parseErrorPayload(text: string): { code?: number; msg?: string } {
  try {
    return JSON.parse(text) as { code?: number; msg?: string };
  } catch {
    return {};
  }
}

function unwrapMcpPayload<T>(json: unknown): T {
  if (
    json &&
    typeof json === 'object' &&
    'data' in json &&
    (json as { data?: T }).data !== undefined
  ) {
    return (json as { data: T }).data;
  }
  return json as T;
}

type McpBaseQueryArgs = string | FetchArgs;

function buildMcpUrl(url: string, params?: FetchArgs['params']): string {
  if (!params || typeof params !== 'object') {
    return `${MCP_API_BASE}${url}`;
  }
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item === undefined || item === null || item === '') continue;
        search.append(key, String(item));
      }
      continue;
    }
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `${MCP_API_BASE}${url}?${qs}` : `${MCP_API_BASE}${url}`;
}

const mcpBaseQuery: BaseQueryFn<
  McpBaseQueryArgs,
  unknown,
  FetchBaseQueryError
> = async (args, _api, _extraOptions) => {
  const run = async (retried: boolean): Promise<
    { data: unknown } | { error: FetchBaseQueryError }
  > => {
    const fetchArgs: FetchArgs =
      typeof args === 'string' ? { url: args } : { ...args };
    const method = (fetchArgs.method ?? 'GET').toUpperCase();
    const hasBody =
      fetchArgs.body !== undefined &&
      fetchArgs.body !== null &&
      fetchArgs.body !== '';
    const body =
      !hasBody && (method === 'POST' || method === 'PUT' || method === 'PATCH')
        ? {}
        : fetchArgs.body;
    const sendJson =
      hasBody ||
      method === 'POST' ||
      method === 'PUT' ||
      method === 'PATCH';

    const headers = new Headers(fetchArgs.headers as HeadersInit | undefined);
    if (sendJson && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    const token = getAccessToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const response = await fetch(buildMcpUrl(fetchArgs.url, fetchArgs.params), {
      method,
      headers,
      body:
        body === undefined || body === null
          ? undefined
          : typeof body === 'string'
            ? body
            : JSON.stringify(body)
    });

    const text = await response.text().catch(() => '');
    const payload = parseErrorPayload(text);

    if (response.status === 401) {
      if (payload.code === 40102 && !retried) {
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          return run(true);
        }
      }
      return {
        error: {
          status: response.status,
          data: payload.msg || text || `HTTP ${response.status}`
        }
      };
    }

    if (!response.ok) {
      return {
        error: {
          status: response.status,
          data: payload.msg || text || `HTTP ${response.status}`
        }
      };
    }

    let json: unknown = null;
    if (text) {
      try {
        json = JSON.parse(text);
      } catch {
        json = text;
      }
    }

    return { data: unwrapMcpPayload(json) };
  };

  return run(false);
};

export function toMcpApiErrorMessage(
  error: unknown,
  fallback = 'خطای ناشناخته'
): string {
  if (
    error &&
    typeof error === 'object' &&
    'data' in error &&
    typeof (error as { data?: unknown }).data === 'string'
  ) {
    return (error as { data: string }).data;
  }
  if (error instanceof Error) return error.message;
  return fallback;
}

const mcpApi = createApi({
  reducerPath: 'mcpApi',
  baseQuery: mcpBaseQuery,
  tagTypes: [
    'McpThreadList',
    'McpThread',
    'McpAiSettings',
    'McpAutoBudget',
    'McpChatOnboarding',
    'McpProjectModels',
    'McpPromptTemplateList',
    'McpPromptTemplate',
    'McpPromptVariables',
    'McpPromptCategories',
    'McpToolTypes',
    'McpChatSkills',
    'McpToolLabels',
    'McpChatSuggestions'
  ],
  endpoints: build => ({
    listMcpThreads: build.query<McpThreadsListResult, ListMcpThreadsArgs | void>(
      {
        query: params => ({
          url: '/chat/threads',
          params: {
            limit: params?.limit ?? 20,
            offset: params?.offset ?? 0,
            ...(params?.q?.trim() ? { q: params.q.trim() } : {})
          }
        }),
        providesTags: result =>
          result
            ? [
                'McpThreadList',
                ...result.items.map(item => ({
                  type: 'McpThread' as const,
                  id: item.id
                }))
              ]
            : ['McpThreadList']
      }
    ),

    getMcpThread: build.query<McpThreadDetail, string>({
      query: id => `/chat/threads/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'McpThread', id }]
    }),

    deleteMcpThread: build.mutation<{ ok: boolean }, string>({
      query: id => ({
        url: `/chat/threads/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: (_r, _e, id) => [
        'McpThreadList',
        { type: 'McpThread', id }
      ]
    }),

    getMcpAiSettings: build.query<McpUserAiSettings, void>({
      query: () => '/ai-settings',
      providesTags: ['McpAiSettings']
    }),

    /** The limit comes from the domain's plan, so the domain slug is required. */
    getMcpAutoBudget: build.query<McpAutoBudget, string>({
      query: (domainSlug) =>
        `/ai-settings/auto-budget?domainSlug=${encodeURIComponent(domainSlug)}`,
      providesTags: ['McpAutoBudget']
    }),

    getMcpChatOnboarding: build.query<McpChatOnboardingState, void>({
      query: () => '/chat/onboarding',
      providesTags: ['McpChatOnboarding']
    }),

    patchMcpChatOnboarding: build.mutation<
      McpChatOnboardingState,
      PatchMcpChatOnboardingArgs
    >({
      query: body => ({
        url: '/chat/onboarding',
        method: 'PATCH',
        body
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            mcpApi.util.updateQueryData(
              'getMcpChatOnboarding',
              undefined,
              () => data
            )
          );
        } catch {
          // keep previous cache on error
        }
      },
      invalidatesTags: ['McpChatOnboarding']
    }),

    updateMcpAiSettings: build.mutation<
      McpUserAiSettings,
      { projectAiModelId: string }
    >({
      query: body => ({
        url: '/ai-settings',
        method: 'PUT',
        body
      }),
      invalidatesTags: ['McpAiSettings']
    }),

    listMcpProjectModels: build.query<McpProjectAiModel[], void>({
      query: () => '/ai-settings/models',
      providesTags: ['McpProjectModels']
    }),

    listMcpProjectModelProviders: build.query<
      McpProjectAiModelProvider[],
      void
    >({
      query: () => '/ai-settings/providers',
      providesTags: ['McpProjectModels']
    }),

    listMcpPromptTemplates: build.query<
      McpPromptTemplatesListResult,
      ListMcpPromptTemplatesArgs | void
    >({
      query: params => {
        const filters = params?.filters
          ? {
              ...(params.filters.q?.trim()
                ? { q: params.filters.q.trim() }
                : {}),
              ...(params.filters.categoryIds?.length
                ? { categoryIds: params.filters.categoryIds }
                : {}),
              ...(params.filters.toolIds?.length
                ? { toolIds: params.filters.toolIds }
                : {})
            }
          : undefined;

        return {
          url: '/prompt-templates/list',
          method: 'POST',
          body: {
            limit: params?.limit ?? 20,
            offset: params?.offset ?? 0,
            ...(params?.scope ? { scope: params.scope } : {}),
            ...(params?.sort ? { sort: params.sort } : {}),
            ...(filters && Object.keys(filters).length > 0 ? { filters } : {})
          }
        };
      },
      providesTags: result =>
        result
          ? [
              'McpPromptTemplateList',
              ...result.items.map(item => ({
                type: 'McpPromptTemplate' as const,
                id: item.id
              }))
            ]
          : ['McpPromptTemplateList']
    }),

    getMcpPromptTemplate: build.query<McpPromptTemplate, string>({
      query: id => `/prompt-templates/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'McpPromptTemplate', id }]
    }),

    createMcpPromptTemplate: build.mutation<
      McpPromptTemplate,
      McpPromptTemplateUpsert
    >({
      query: body => ({
        url: '/prompt-templates',
        method: 'POST',
        body
      }),
      invalidatesTags: ['McpPromptTemplateList']
    }),

    updateMcpPromptTemplate: build.mutation<
      McpPromptTemplate,
      { id: string; body: Partial<McpPromptTemplateUpsert> }
    >({
      query: ({ id, body }) => ({
        url: `/prompt-templates/${id}`,
        method: 'PUT',
        body
      }),
      invalidatesTags: (_r, _e, { id }) => [
        'McpPromptTemplateList',
        { type: 'McpPromptTemplate', id }
      ]
    }),

    deleteMcpPromptTemplate: build.mutation<{ ok: boolean }, string>({
      query: id => ({
        url: `/prompt-templates/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: (_r, _e, id) => [
        'McpPromptTemplateList',
        { type: 'McpPromptTemplate', id }
      ]
    }),

    likeMcpPromptTemplate: build.mutation<
      { likedByMe: boolean; likesCount: number },
      string
    >({
      query: id => ({
        url: `/prompt-templates/${id}/like`,
        method: 'POST'
      }),
      invalidatesTags: (_r, _e, id) => [
        'McpPromptTemplateList',
        { type: 'McpPromptTemplate', id }
      ]
    }),

    listMcpPromptVariables: build.query<McpPromptVariable[], void>({
      query: () => '/prompt-templates/variables',
      providesTags: ['McpPromptVariables']
    }),

    listMcpPromptCategories: build.query<McpPromptCategory[], void>({
      query: () => '/prompt-templates/categories',
      providesTags: ['McpPromptCategories']
    }),

    listMcpToolTypes: build.query<McpToolType[], void>({
      query: () => '/prompt-templates/tool-types',
      providesTags: ['McpToolTypes']
    }),

    listMcpChatSkills: build.query<McpChatSkillsListResult, void>({
      query: () => '/chat/skills',
      providesTags: ['McpChatSkills']
    }),

    listMcpChatSkillsCatalog: build.query<
      McpChatSkillCatalogResult,
      ListMcpChatSkillsCatalogArgs | void
    >({
      query: (args = {}) => ({
        url: '/chat/skills/catalog',
        params: {
          ...(args?.q ? { q: args.q } : {}),
          ...(args?.category ? { category: args.category } : {}),
          ...(args?.limit != null ? { limit: args.limit } : {}),
          ...(args?.offset != null ? { offset: args.offset } : {})
        }
      }),
      providesTags: ['McpChatSkills']
    }),

    listMcpChatSkillCategories: build.query<
      McpChatSkillCategoriesResult,
      void
    >({
      query: () => '/chat/skills/categories',
      providesTags: ['McpChatSkills']
    }),

    listMcpToolLabels: build.query<McpToolLabelsResult, void>({
      query: () => '/chat/tools/labels',
      providesTags: ['McpToolLabels']
    }),

    /** Chat welcome suggestions (سوالات متداول چت) — not help/FAQ_AI. */
    listMcpChatSuggestions: build.query<McpChatSuggestionsResult, void>({
      query: () => '/chat/suggestions',
      providesTags: ['McpChatSuggestions']
    }),

    askMcpHelp: build.mutation<
      McpHelpAskResult,
      { question: string; threadId?: string }
    >({
      query: body => ({
        url: '/chat/help/ask',
        method: 'POST',
        body
      }),
      invalidatesTags: ['McpThreadList']
    }),

    getMcpBackgroundTool: build.query<
      {
        channelId: string;
        phase: string;
        message: string;
        percent: number;
        isDone: boolean;
        failed: boolean;
        summary: string | null;
        chart: {
          title?: string;
          type?: 'bar' | 'line' | 'pie' | 'area';
          data?: Array<Record<string, string | number>>;
          xKey?: string;
          dataKeys?: string[];
        } | null;
      },
      string
    >({
      query: channelId => `/chat/tools/background/${channelId}`
    }),

    getMcpSkillExecution: build.query<
      {
        id: string;
        status: string;
        phase: string | null;
        message: string | null;
        error: string | null;
        isDone: boolean;
        resultContent: string | null;
      },
      string
    >({
      query: id => `/chat/skills/executions/${id}`
    }),

    cancelMcpSkillExecution: build.mutation<
      {
        id: string;
        status: string;
        phase: string | null;
        message: string | null;
        error: string | null;
        isDone: boolean;
        resultContent: string | null;
      },
      string
    >({
      query: id => ({
        url: `/chat/skills/executions/${id}/cancel`,
        method: 'POST'
      })
    }),

    upsertMcpMessageFeedback: build.mutation<
      McpMessageFeedback,
      {
        messageId: string;
        rating: McpMessageFeedbackRating;
        reasons?: McpMessageFeedbackReason[];
        details?: string;
      }
    >({
      query: body => ({
        url: '/chat/message-feedback',
        method: 'POST',
        body
      })
    })
  })
});

export const {
  useListMcpThreadsQuery,
  useLazyListMcpThreadsQuery,
  useGetMcpThreadQuery,
  useLazyGetMcpThreadQuery,
  useDeleteMcpThreadMutation,
  useGetMcpAiSettingsQuery,
  useGetMcpAutoBudgetQuery,
  useUpdateMcpAiSettingsMutation,
  useGetMcpChatOnboardingQuery,
  usePatchMcpChatOnboardingMutation,
  useListMcpProjectModelsQuery,
  useListMcpProjectModelProvidersQuery,
  useListMcpPromptTemplatesQuery,
  useLazyListMcpPromptTemplatesQuery,
  useGetMcpPromptTemplateQuery,
  useLazyGetMcpPromptTemplateQuery,
  useCreateMcpPromptTemplateMutation,
  useUpdateMcpPromptTemplateMutation,
  useDeleteMcpPromptTemplateMutation,
  useLikeMcpPromptTemplateMutation,
  useListMcpPromptVariablesQuery,
  useListMcpPromptCategoriesQuery,
  useListMcpToolTypesQuery,
  useListMcpChatSkillsQuery,
  useListMcpChatSkillsCatalogQuery,
  useLazyListMcpChatSkillsCatalogQuery,
  useListMcpChatSkillCategoriesQuery,
  useListMcpToolLabelsQuery,
  useListMcpChatSuggestionsQuery,
  useGetMcpBackgroundToolQuery,
  useGetMcpSkillExecutionQuery,
  useCancelMcpSkillExecutionMutation,
  useAskMcpHelpMutation,
  useUpsertMcpMessageFeedbackMutation
} = mcpApi;

/** Invalidate thread list/detail caches (e.g. after local history events). */
export async function invalidateMcpApiThreadTags() {
  const { store } = await import('@/store/store');
  store.dispatch(mcpApi.util.invalidateTags(['McpThreadList', 'McpThread']));
}

export default mcpApi;
